import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FolderIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Project } from '@/types/supabase';
import { useAuth } from '@/contexts/auth/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { fetchCollaborativeProjects } from '@/services/project/projectService';

const ProjectsList = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [collaborativeProjects, setCollaborativeProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadProjects();
      loadCollaborativeProjects();
    }
  }, [user]);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast({
        title: "Error loading projects",
        description: "There was a problem loading your projects.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadCollaborativeProjects = async () => {
    try {
      const collaborations = await fetchCollaborativeProjects();
      setCollaborativeProjects(collaborations);
    } catch (error) {
      console.error('Error loading collaborative projects:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <h1 className="text-2xl font-bold">Projects</h1>
          <p className="text-muted-foreground">Manage your bootcamp projects</p>
        </div>
        <Button asChild className="mt-2 md:mt-0">
          <Link to="/projects/new">Create New Project</Link>
        </Button>
      </div>
      
      <div className="space-y-8">
        <div>
          <h2 className="text-xl font-semibold mb-4">Your Projects</h2>
          {projects.length === 0 ? (
            <Card>
              <CardContent className="py-6">
                <div className="text-center">
                  <FolderIcon className="h-12 w-12 mx-auto text-muted-foreground" />
                  <h3 className="mt-2 text-lg font-medium">No projects yet</h3>
                  <p className="text-muted-foreground mt-1">
                    Create your first project to get started.
                  </p>
                  <Button asChild className="mt-4">
                    <Link to="/projects/new">Create Project</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <Link key={project.id} to={`/projects/${project.id}`}>
                  <Card className="h-full transition-all hover:shadow-md">
                    <CardHeader>
                      <CardTitle className="flex justify-between items-start">
                        <span className="truncate">{project.title}</span>
                        {project.score !== null && (
                          <Badge variant="outline" className="ml-2 whitespace-nowrap">
                            Score: {project.score}/100
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>
                        Created on {new Date(project.created_at).toLocaleDateString()}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="line-clamp-2 text-sm text-muted-foreground">
                        {project.description || 'No description provided.'}
                      </p>
                    </CardContent>
                    <CardFooter>
                      <div className="flex justify-between items-center w-full">
                        <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300 font-medium">
                          Owner
                        </Badge>
                        {project.submission_url ? (
                          <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300 font-medium">
                            Submitted
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 font-medium">
                            Draft
                          </Badge>
                        )}
                      </div>
                    </CardFooter>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>

        {collaborativeProjects.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Projects You're Collaborating On</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {collaborativeProjects.map((project) => (
                <Link key={project.id} to={`/projects/${project.id}`}>
                  <Card className="h-full transition-all hover:shadow-md">
                    <CardHeader>
                      <CardTitle className="flex justify-between items-start">
                        <span className="truncate">{project.title}</span>
                        {project.score !== null && (
                          <Badge variant="outline" className="ml-2 whitespace-nowrap">
                            Score: {project.score}/100
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>
                        Created on {new Date(project.created_at).toLocaleDateString()}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="line-clamp-2 text-sm text-muted-foreground">
                        {project.description || 'No description provided.'}
                      </p>
                    </CardContent>
                    <CardFooter>
                      <div className="flex justify-between items-center w-full">
                        <Badge variant="outline" className={
                          project.role === 'editor' 
                            ? "bg-blue-100 text-blue-800 border-blue-300 font-medium"
                            : "bg-gray-100 text-gray-800 border-gray-300 font-medium"
                        }>
                          {project.role === 'editor' ? 'Editor' : 'Viewer'}
                        </Badge>
                        {project.submission_url ? (
                          <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300 font-medium">
                            Submitted
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 font-medium">
                            Draft
                          </Badge>
                        )}
                      </div>
                    </CardFooter>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsList;
